import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Box, InputRadio, Switch, Typography } from 'shared/ui'
import {
  ChatbotMessageLength,
  ChatbotToneOfVoice,
  type IChatbotMessageLength,
  type IChatbotToneOfVoice,
} from 'entities/Chatbot'
import { AutoFollowUp } from 'pages/chatbot/ui/FollowUpSettings/AutoFollowUp'
import { type AdvancedSettingsStore } from 'pages/chatbot/pages/agentBookAppointment/store/advancedSettingsStore'
import { type ITabsListItem, SettingsTabs } from 'pages/chatbot/ui/FollowUpSettings/SettingsTabs'
import { DaysDropdown } from 'pages/chatbot/ui/FollowUpSettings/DaysDropdown'
import styles from './styles.module.scss'

export const GeneralSettings = observer(({ settings }: { settings: AdvancedSettingsStore }) => {
  const {
    followUpWindow,
    followUpAssertiveness,
    setFollowUpWindow,
    setFollowUpAssertiveness,
    toneOfVoice,
    setToneOfVoice,
    messageLength,
    setMessageLength,
    availabilities,
    availability,
    autoFollowUpEnabled,
    onAutoFollowUpToggle,
    sessionLifetime,
    setSessionLifetime,
  } = settings

  const toneOfVoiceList: ITabsListItem<IChatbotToneOfVoice>[] = [
    {
      value: ChatbotToneOfVoice.Fun,
      label: 'Fun',
      icon: 'rocket',
    },
    {
      value: ChatbotToneOfVoice.Friendly,
      label: 'Friendly',
      icon: 'smile',
    },
    {
      value: ChatbotToneOfVoice.Professional,
      label: 'Professional',
      icon: 'case',
    },
  ]

  const messageLengthList: ITabsListItem<IChatbotMessageLength>[] = [
    {
      value: ChatbotMessageLength.Brief,
      label: 'Brief',
      icon: 'oneLine',
    },
    {
      value: ChatbotMessageLength.Standard,
      label: 'Standard',
      icon: 'twoLines',
    },
    {
      value: ChatbotMessageLength.Extended,
      label: 'Extended',
      icon: 'threeLines',
    },
  ]

  return (
    <div className={styles.followUp}>
      <Box display={'flex'}>
        <Box display={'flex'} flexDirection={'column'} flexGrow={'1'}>
          <Typography variant={'body-lg-medium'} ariaLabel={'AutoFollowUp'}>
            Auto FollowUp™
          </Typography>
          <Typography
            variant={'body-md-regular'}
            ariaLabel={'AutoFollowUpDesc'}
            color={'var(--content-primary-secondary)'}
            marginTop={4}
          >
            AI-powered follow-ups to maximize engagement
          </Typography>
        </Box>
        <Box paddingLeft={'20px'} paddingTop={'4px'}>
          <Switch value={autoFollowUpEnabled} onChange={onAutoFollowUpToggle} size='middle' />
        </Box>
      </Box>
      {autoFollowUpEnabled && (
        <AutoFollowUp
          followUpWindow={followUpWindow}
          followUpAssertiveness={followUpAssertiveness}
          setFollowUpWindow={setFollowUpWindow}
          setFollowUpAssertiveness={setFollowUpAssertiveness}
          tabsClassName={styles.tabsItems}
        />
      )}
      <Typography variant={'body-lg-medium'} ariaLabel={'EndSession'} marginTop={32}>
        End session
      </Typography>
      {autoFollowUpEnabled ? (
        <>
          <Typography
            variant={'body-md-regular'}
            ariaLabel={'EndSessionDesc'}
            color={'var(--content-primary-secondary)'}
            marginTop={4}
            marginBottom={8}
          >
            If the contact doesn’t reply within 1 day after the follow-up window
          </Typography>
        </>
      ) : (
        <DaysDropdown
          label='If the contact doesn’t reply within'
          followUpWindow={sessionLifetime}
          setFollowUpWindow={setSessionLifetime}
          days={Array(30)
            .fill(0)
            .map((_, index) => ({
              value: index + 1,
              label: String(index + 1),
            }))}
        />
      )}
      <Typography
        variant={'body-lg-medium'}
        ariaLabel={'AgentTone'}
        marginTop={autoFollowUpEnabled ? 32 : 16}
      >
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Agent's tone of voice
      </Typography>
      <Typography
        variant={'body-md-regular'}
        ariaLabel={'AgentToneDesc'}
        color={'var(--content-primary-secondary)'}
        marginTop={4}
        marginBottom={16}
      >
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Set the Agent's messaging tone
      </Typography>
      <SettingsTabs
        tabsList={toneOfVoiceList}
        selectedValue={toneOfVoice}
        setSelectedValue={(value) => setToneOfVoice(value)}
        tabsClassName={styles.tabsItems}
      />
      <Typography variant={'body-lg-medium'} ariaLabel={'AgentMessageLength'} marginTop={32}>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Agent's message length
      </Typography>
      <Typography
        variant={'body-md-regular'}
        ariaLabel={'AgentMessageLengthDesc'}
        color={'var(--content-primary-secondary)'}
        marginTop={4}
        marginBottom={16}
      >
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Set how long the Agent's responses are
      </Typography>
      <SettingsTabs
        tabsList={messageLengthList}
        selectedValue={messageLength}
        setSelectedValue={(value) => setMessageLength(value)}
        tabsClassName={styles.tabsItems}
      />
      <Typography variant={'body-lg-medium'} ariaLabel={'AgentAvailability'} marginTop={32}>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Agent's availability
      </Typography>
      <Typography
        variant={'body-md-regular'}
        ariaLabel={'AgentAvailabilityDesc'}
        color={'var(--content-primary-secondary)'}
        marginTop={4}
      >
        Set when the agent can respond to messages
      </Typography>
      <Box display={'flex'} flexDirection={'column'} marginTop={1}>
        {availabilities.map((item) => (
          <InputRadio
            key={item.id}
            label={item.customLabel}
            checked={availability === item.id}
            onChecked={() => {}}
            className={classNames(styles.radioButton, {
              [styles.checked]: availability === item.id,
              [styles.disabled]: item.disabled,
            })}
            radioSize='small'
            disabled={item.disabled}
          />
        ))}
      </Box>
    </div>
  )
})
